/***************************************************************************
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import { A3dViewerElement } from '@a3d-viewer/viewer'
import { useEffect, useRef, useState } from 'react'
import { ModelType } from '../App'
import ErrorDialog from '../ErrorDialog'
import DropZone from './DropZone'

import { ModelLoadingError } from '@a3d-viewer/viewer'
import { useAtom } from 'jotai'
import { DefaultViewerConfig, viewerAtom } from '../config'
import useReadQueryParams from '../hooks/useReadQueryParams'
import ViewerWC from './ViewerWC'

type ViewerProps = {
  onDomChange?: (viewerElement: A3dViewerElement) => void
  onStateChange?: (state: string) => void
}

export default function Viewer({ onDomChange }: ViewerProps) {
  const [viewer, setViewer] = useAtom(viewerAtom)
  const [model, setModel] = useState<ModelType | null>(null)
  const viewerMainContainerRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<boolean>(false)
  const { modelUrl } = useReadQueryParams()

  const handleDrop = ({ fileAsDataUrl, extension }: ModelType) => {
    setModel({ fileAsDataUrl, extension })
  }

  useEffect(() => {
    if (modelUrl) {
      setModel({ fileAsDataUrl: modelUrl })
    }
  }, [modelUrl])

  const handleError = (e: Event) => {
    const { detail } = e as CustomEvent

    switch (detail.name) {
      case ModelLoadingError.name:
        setViewer(DefaultViewerConfig)
        setModel(null)
        setError(true)
        break
    }
  }

  const toggleFullscreen = () => {
    if (viewer.fullscreen) {
      setViewer({
        ...viewer,
        fullscreen: false,
      })
      document.exitFullscreen()
    } else {
      setViewer({
        ...viewer,
        fullscreen: true,
      })
      viewerMainContainerRef.current?.requestFullscreen()
    }
  }

  return (
    <div
      className="relative h-[80svh] sm:h-auto sm:aspect-video sm:max-h-[70vh]"
      ref={viewerMainContainerRef}
    >
      <DropZone onDrop={handleDrop} showDraggingText={model === null}>
        <ViewerWC
          key={model?.fileAsDataUrl}
          model={model}
          toggleFullscreen={toggleFullscreen}
          onDomChange={onDomChange}
          onError={handleError}
        />
      </DropZone>

      <ErrorDialog isOpen={error} onDismiss={() => setError(false)} />
    </div>
  )
}
